import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useCreatePortfolioOnboardingInvitationMutation } from '@client/api';
import { Button, Input } from '@client/toolkit';
import { useBrregCompanySearch, BrregCompanyData } from '../../companies/hooks/useBrregCompanySearch';
import { useIsExioStaff } from '../../../hooks/useIsExioStaff';

interface InvitationResult {
  invitationId: string;
  email: string;
  clerkInvitationSent: boolean;
}

interface PendingConfirmation {
  email: string;
  companyOrgNr: string;
  companyName: string;
  existingTenantId?: string;
}

export function AdminInviteRoute() {
  const { isLoaded: isAuthLoaded, isStaff } = useIsExioStaff();
  const [email, setEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<BrregCompanyData | null>(null);
  const [searchResults, setSearchResults] = useState<BrregCompanyData[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const skipAutoSearchRef = useRef(false);
  const { search: searchBrreg, isLoading: isSearching } = useBrregCompanySearch();
  const [createInvitation, { isLoading }] = useCreatePortfolioOnboardingInvitationMutation();
  const [results, setResults] = useState<InvitationResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  // Tracks which invitation row just had its onboarding URL copied so we can flip
  // the button label to "Kopiert!" for ~2s. Null = nothing copied right now.
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const performSearch = useCallback(
    async (query: string) => {
      if (!query || !query.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }
      const result = await searchBrreg(query);
      if (result) {
        setSearchResults(result);
        setShowDropdown(true);
        setHighlightedIndex(-1);
      }
    },
    [searchBrreg],
  );

  const handleSelectCompany = useCallback((company: BrregCompanyData) => {
    skipAutoSearchRef.current = true;
    setSelectedCompany(company);
    setSearchQuery(company.navn);
    setShowDropdown(false);
    setSearchResults([]);
  }, []);

  const handleClearCompany = () => {
    setSelectedCompany(null);
    setSearchQuery('');
    setSearchResults([]);
    setEmail('');
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (selectedCompany && value !== selectedCompany.navn) {
      setSelectedCompany(null);
      skipAutoSearchRef.current = false;
    }
    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      setSelectedCompany(null);
    }
  };

  useEffect(() => {
    if (skipAutoSearchRef.current) {
      skipAutoSearchRef.current = false;
      return;
    }
    if (searchQuery.trim().length < 3) return;
    const timer = setTimeout(() => performSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown && searchResults.length > 0) {
        setHighlightedIndex((i) => Math.min(i + 1, searchResults.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown && searchResults.length > 0) {
        setHighlightedIndex((i) => Math.max(i - 1, 0));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && highlightedIndex >= 0 && searchResults[highlightedIndex]) {
        handleSelectCompany(searchResults[highlightedIndex]);
      } else {
        performSearch(searchQuery);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const sendInvitation = async (
    payload: { email: string; companyOrgNr: string; companyName: string },
    confirmReset: boolean,
  ) => {
    const result = await createInvitation({
      body: {
        email: payload.email,
        companyOrgNr: payload.companyOrgNr,
        companyName: payload.companyName,
        confirmReset,
      },
    }).unwrap();

    setResults((prev) => [result, ...prev]);
    setEmail('');
    setSelectedCompany(null);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    skipAutoSearchRef.current = false;
    setPendingConfirmation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedCompany) {
      setError('Velg et selskap først');
      return;
    }
    if (!email.trim()) {
      setError('E-post er påkrevd');
      return;
    }

    const payload = {
      email: email.trim(),
      companyOrgNr: selectedCompany.organisasjonsnummer,
      companyName: selectedCompany.navn,
    };

    try {
      await sendInvitation(payload, false);
    } catch (err: any) {
      // 409 ALREADY_ONBOARDED → prompt admin to confirm reset
      if (err?.status === 409 && err?.data?.code === 'ALREADY_ONBOARDED') {
        setPendingConfirmation({
          ...payload,
          existingTenantId: err?.data?.existingTenantId,
        });
        return;
      }
      // 409 TENANT_EXISTS → a tenant for this org already exists; not recoverable here
      if (err?.status === 409 && err?.data?.code === 'TENANT_EXISTS') {
        setError(
          `Det finnes allerede en aktiv tenant for organisasjonsnummer ${payload.companyOrgNr}. ` +
          'Invitasjonen ble avvist for å unngå duplikater.',
        );
        return;
      }
      setError(err?.data?.detail ?? err?.message ?? 'En feil oppstod');
    }
  };

  const handleConfirmReset = async () => {
    if (!pendingConfirmation) return;
    setError(null);
    try {
      await sendInvitation(pendingConfirmation, true);
    } catch (err: any) {
      setError(err?.data?.detail ?? err?.message ?? 'En feil oppstod');
      setPendingConfirmation(null);
    }
  };

  const handleCancelReset = () => {
    setPendingConfirmation(null);
  };

  // Building the onboarding URL on the client side keeps the backend free of presentation
  // concerns and guarantees the admin copies a link on the same origin they're logged in on
  // (so e.g. beta admins share beta URLs, prod admins share prod URLs).
  const getOnboardingUrl = () => `${window.location.origin}/onboarding/portfolio`;

  const handleCopyUrl = async (invitationId: string) => {
    try {
      await navigator.clipboard.writeText(getOnboardingUrl());
      setCopiedInvitationId(invitationId);
      // Reset the label back to "Kopier URL" after 2s so repeated copies are obvious.
      window.setTimeout(() => {
        setCopiedInvitationId((current) => (current === invitationId ? null : current));
      }, 2000);
    } catch {
      // Clipboard API unavailable (e.g. non-secure context) — surface via the error banner
      // so the admin knows to copy manually from the shown URL.
      setError('Kunne ikke kopiere automatisk. Kopier URL manuelt.');
    }
  };

  // Route guard: only Exio staff (@exio.no) can access this page
  if (!isAuthLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-primary rounded-full animate-spin border-r-transparent" />
      </div>
    );
  }
  if (!isStaff) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-2">Inviter til portefølje-onboarding</h1>
        <p className="text-white/60 mb-8">
          Søk opp selskapet og send en invitasjon til brukeren som skal starte portefølje-onboarding.
        </p>

        <form noValidate onSubmit={handleSubmit} className="space-y-4 mb-8">
          <div className="relative">
            <label className="block text-sm font-medium text-white/80 mb-1">Selskap *</label>

            {selectedCompany ? (
              <div className="flex items-center gap-3 p-3 rounded-lg border border-brand-primary bg-surface-selected">
                <div className="flex-1">
                  <span className="text-white font-medium">{selectedCompany.navn}</span>
                  <span className="text-white/50 ml-2 text-sm">
                    Org.nr {selectedCompany.organisasjonsnummer}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleClearCompany}
                  className="text-white/40 hover:text-white text-lg leading-none"
                >
                  &times;
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => {
                    if (searchResults.length > 0 && !selectedCompany) setShowDropdown(true);
                  }}
                  placeholder="Søk på selskapsnavn eller org.nr"
                  className="w-full"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-brand-primary rounded-full animate-spin border-r-transparent" />
                  </div>
                )}

                {showDropdown && !selectedCompany && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-[#201E1A] rounded shadow-lg max-h-72 overflow-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#322F2C] [&::-webkit-scrollbar-thumb]:rounded"
                  >
                    {searchResults.length > 0 ? (
                      searchResults.map((company, index) => (
                        <button
                          key={company.organisasjonsnummer}
                          type="button"
                          onClick={() => handleSelectCompany(company)}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                            index < searchResults.length - 1 ? 'border-b border-[#322F2C]' : ''
                          } ${highlightedIndex === index ? 'bg-[#272521]' : 'hover:bg-[#272521]'}`}
                        >
                          <div className="text-white">{company.navn}</div>
                          <div className="text-white/50 text-xs">Org.nr {company.organisasjonsnummer}</div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-white/50">Ingen resultater</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedCompany && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">E-post *</label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="bruker@eksempel.no"
                type="email"
                className="w-full"
                autoFocus
              />
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded p-3 text-red-300 text-sm">
              {error}
            </div>
          )}

          {selectedCompany && (
            <Button type="submit" disabled={isLoading || !email.trim()} className="w-full">
              {isLoading ? 'Sender...' : 'Send invitasjon'}
            </Button>
          )}
        </form>

        {results.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Brukere aktivert</h2>
            <div className="space-y-2">
              {results.map((r) => {
                const justCopied = copiedInvitationId === r.invitationId;
                return (
                  <div
                    key={r.invitationId}
                    className="bg-surface-elevated/50 rounded p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium truncate">{r.email}</p>
                        <p className="text-white/40 text-xs truncate">ID: {r.invitationId}</p>
                      </div>
                      {/*
                        Copy-URL is available for every row: for new users it doubles as a
                        convenience share link (Slack/SMS); for already-registered users it
                        is the *only* way the invitee receives the onboarding URL, since
                        Clerk refuses to send a fresh invitation email in that case.
                      */}
                      <button
                        type="button"
                        onClick={() => handleCopyUrl(r.invitationId)}
                        className={`shrink-0 text-sm px-3 py-1 rounded border transition-colors ${
                          justCopied
                            ? 'bg-green-500/20 border-green-500/50 text-green-300'
                            : 'bg-transparent border-white/20 text-white/80 hover:bg-white/10'
                        }`}
                        title={getOnboardingUrl()}
                      >
                        {justCopied ? 'Kopiert!' : 'Kopier URL'}
                      </button>
                    </div>
                    {r.clerkInvitationSent ? (
                      <p className="text-xs text-white/50">
                        E-post er sendt automatisk til brukeren.
                      </p>
                    ) : (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-sm text-amber-200">
                        <p className="font-medium mb-1">Brukeren er allerede registrert i Exio.</p>
                        <p className="text-amber-200/80">
                          Brukeren er allerede onboardet, kopier URL og send denne til brukeren for ny onboarding.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {pendingConfirmation && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="already-onboarded-title"
        >
          <div className="bg-[#201E1A] rounded-lg shadow-xl max-w-md w-full p-6">
            <h2
              id="already-onboarded-title"
              className="text-xl font-semibold text-white mb-3"
            >
              Bruker er allerede onboarded
            </h2>
            <p className="text-white/70 mb-6">
              Brukeren{' '}
              <span className="text-white font-medium">{pendingConfirmation.email}</span> er
              allerede onboarded. Ønsker du å sende en ny invitasjon og onboarde et nytt selskap
              med samme bruker?
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                onClick={handleCancelReset}
                disabled={isLoading}
                color="primary-dark"
              >
                Avbryt
              </Button>
              <Button type="button" onClick={handleConfirmReset} disabled={isLoading}>
                {isLoading ? 'Sender...' : 'Ja, send ny invitasjon'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
